import {
	ReactElement,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { AnnouncementFilterState, useDataSource } from "../useDataSource";
import {
	MediaQueryKind,
	RemoteDataSourceState,
	useDeepComparison,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	DataSourceSortDirection,
	DataSourceSortableState
} from "client/Data/sorting";
import { AnnouncementPreviewItemModel } from "client/Api/Hub/Announcements/announcementViewer";
import {
	BusyIndicator,
	Button,
	NoResults
} from "client/Components";
import { List } from "../List/List";
import { localize } from "client_legacy/ndaw/culture";
import { isNil } from "client/Misc/utils";
import styles from "./OverviewSections.module.less";
import {
	HubOverviewLayout
} from "client/Features/Hub/HubOverviewLayout";
import { HubListButton } from "client/Features/Hub/KnowledgeItems/CategoryOverview/List/HubItem/HubListButton";
import { ActiveFilter, HubItemIsNewFilter } from "client/Features/Hub/common";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	HubEvent,
	subscribe,
	unsubscribe
} from "client/Features/Hub/events";
import { HubDataSource } from "client/Api/Hub/hub";

export type OverviewSectionsProps = {
	selectedCategoryId: number | null;
	activeFilter: ActiveFilter | null;
	searchTerm: string;
	sorting: DataSourceSortableState<AnnouncementPreviewItemModel>;
	hasFloatingButton: boolean;
	onNavigateToItem(id: number): void;
	onScrollToTop(): void;
	timezone: TimeZone;
	isNewFilter: HubItemIsNewFilter;
};

export const OverviewSections = (props: OverviewSectionsProps): ReactElement => {
	const {
		selectedCategoryId,
		activeFilter,
		searchTerm,
		sorting,
		onNavigateToItem,
		hasFloatingButton,
		onScrollToTop,
		timezone,
		isNewFilter
	} = props;

	const [isPinnedCollapsed, setIsPinnedCollapsed] = useState<boolean>(true);
	const [desiredAmountOfOtherItems, setDesiredAmountOfOtherItems] = useState<number>(PAGE_SIZE);
	const [desiredAmountOfPinnedItems, setDesiredAmountOfPinnedItems] = useState<number>(PAGE_SIZE);
	const [newStatusClearedItemIds, setNewStatusClearedItemIds] = useState<Set<number>>(new Set());

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const filtering = useMemo(
		(): AnnouncementFilterState => {
			return {
				categoryId: selectedCategoryId,
				activeFilter,
				isPinned: false,
				isNewFilter
			};
		},
		[
			activeFilter,
			selectedCategoryId,
			isNewFilter
		]
	);

	const filtersVersion: number = useDeepComparison([
		searchTerm,
		filtering,
		sorting
	]);

	useEffect(
		(): void => {
			setDesiredAmountOfOtherItems(PAGE_SIZE);
			setDesiredAmountOfPinnedItems(PAGE_SIZE);
			setIsPinnedCollapsed(true);
			setNewStatusClearedItemIds(new Set());
		},
		[filtersVersion]
	);

	const {
		currentItems: currentOtherItems,
		hasMoreOnServer: hasMoreOtherItemsOnServer,
		getLoadingState: getOtherItemsLoadingState,
		hasLazyLoadError: hasOtherItemsLazyLoadError,
		retryToLoadLastPage: retryToLoadLastOtherItemsPage,
		reload: reloadOtherItems
	} = useDataSource(
		{
			desiredAmountOfItems: desiredAmountOfOtherItems,
			filtering,
			sorting,
			searchTerm
		}
	);

	const {
		currentItems: currentPinnedItems,
		hasMoreOnServer: hasMorePinnedItemsOnServer,
		getLoadingState: getPinnedLoadingState,
		hasLazyLoadError: hasPinnedItemsLazyLoadError,
		reload: reloadPinnedItems,
		retryToLoadLastPage: retryToLoadLastPinnedItemsPage
	} = useDataSource({
		desiredAmountOfItems: desiredAmountOfPinnedItems,
		filtering: {
			...filtering,
			isPinned: true
		},
		sorting: selectedCategoryId
			? pinDateSorting
			: sorting,
		searchTerm
	});

	const [
		currentOtherItemsToRender,
		currentPinnedItemsToRender
	] = useMemo(
		(): [
			currentOtherItemsToRender: readonly AnnouncementPreviewItemModel[],
			currentPinnedItemsToRender: readonly AnnouncementPreviewItemModel[]
		] => {
			return [
				currentOtherItems.map(
					x => newStatusClearedItemIds.has(x.id)
						? {
							...x,
							isNew: false
						}
						: x
				),
				currentPinnedItems.map(
					x => newStatusClearedItemIds.has(x.id)
						? {
							...x,
							isNew: false
						}
						: x
				)
			];
		},
		[
			currentOtherItems,
			currentPinnedItems,
			newStatusClearedItemIds
		]
	);

	useEffect(
		(): (() => void) => {
			const isNewStatusClearedToken: PubSubSubscriptionToken = subscribe(
				HubEvent.IsNewStatusCleared,
				(_, data) => {
					if (data.kind !== HubDataSource.Announcement) {
						return;
					}

					setNewStatusClearedItemIds(
						viewedItemIds => {
							const newSet: Set<number> = new Set(viewedItemIds);
							return newSet.add(data.itemId);
						}
					);
				}
			);

			return (): void => unsubscribe(isNewStatusClearedToken);
		},
		[]
	);

	const isRenderingOfPinnedItemsRestricted: boolean = !searchTerm && isPinnedCollapsed;
	const hasPinnedItems: boolean = currentPinnedItemsToRender.length > 0;

	const otherItemsLoadingState: RemoteDataSourceState = getOtherItemsLoadingState();
	const pinnedLoadingState: RemoteDataSourceState = getPinnedLoadingState();

	const increaseAmount = useCallback(
		(
			setDesiredAmountOfItems: (value: SetStateAction<number>) => void,
			loadingState: RemoteDataSourceState,
			hasMoreOnServer: boolean,
			hasLazyLoadError: boolean
		): void => {
			const isLoading: boolean = loadingState === "loading";

			if (!isLoading
				&& hasMoreOnServer
				&& !hasLazyLoadError
			) {
				setDesiredAmountOfItems(prevState => prevState + PAGE_SIZE);
			}
		},
		[]
	);

	const loadMorePinnedHandler = useCallback(
		(): void => {
			increaseAmount(
				setDesiredAmountOfPinnedItems,
				pinnedLoadingState,
				hasMorePinnedItemsOnServer,
				hasPinnedItemsLazyLoadError
			);
		},
		[
			hasMorePinnedItemsOnServer,
			hasPinnedItemsLazyLoadError,
			increaseAmount,
			pinnedLoadingState
		]
	);

	const loadMoreOtherHandler = useCallback(
		(): void => {
			increaseAmount(
				setDesiredAmountOfOtherItems,
				otherItemsLoadingState,
				hasMoreOtherItemsOnServer,
				hasOtherItemsLazyLoadError
			);
		},
		[
			hasMoreOtherItemsOnServer,
			hasOtherItemsLazyLoadError,
			increaseAmount,
			otherItemsLoadingState
		]
	);

	const toggleCollapsePinnedHandler = useCallback(
		(): void => {
			if (!isPinnedCollapsed) {
				onScrollToTop();
				setIsPinnedCollapsed(true);
			} else {
				setIsPinnedCollapsed(false);
			}
		},
		[
			isPinnedCollapsed,
			onScrollToTop
		]
	);

	const reloadHandler = useCallback(
		(): void => {
			reloadOtherItems();
			reloadPinnedItems();
		},
		[
			reloadOtherItems,
			reloadPinnedItems
		]
	);

	const hasMorePinnedItems: boolean = hasMorePinnedItemsOnServer
		&& !hasPinnedItemsLazyLoadError
		&& !isRenderingOfPinnedItemsRestricted;

	if (currentOtherItemsToRender.length + currentPinnedItemsToRender.length > 0) {
		return (
			<>
				{hasPinnedItems && (
					<HubOverviewLayout.Section trackingId="pinned-section">
						<HubOverviewLayout.SectionTitle trackingId="pinned-section-title">
							{localize("_Pinned")}
						</HubOverviewLayout.SectionTitle>
						<List
							trackingId="announcement-list-pinned"
							items={currentPinnedItemsToRender}
							onNavigateToItem={onNavigateToItem}
							isBusy={getPinnedLoadingState() === "loading"}
							hasMoreItems={hasMorePinnedItems}
							onLoadMore={loadMorePinnedHandler}
							timezone={timezone}
							{...(
								searchTerm
									? {
										collapseMode: undefined
									}
									: {
										collapseMode: {
											kind: isMobile
												? "limited-items"
												: "one-row",
											isCollapsed: isPinnedCollapsed,
											maxItemsCount: isMobile
												? DEFAULT_PINNED_ITEMS_MOBILE_COUNT
												: MAX_PINNED_ITEMS_IN_A_ROW_COUNT
										},
										renderMoreButton: (
											<MoreButton
												isExpanded={!isPinnedCollapsed}
												onClick={toggleCollapsePinnedHandler}
												isMobile={isMobile}
											/>
										)
									}
							)}
						/>
						{hasPinnedItemsLazyLoadError && (
							<NoResults
								trackingId="has-lazy-loading-error"
								icon="gear"
								title={localize("_UnableToRetrieveItems")}
								message={localize("_WeCantRetrieveAnyItemsAtThisMomentPleaseRetry")}
								onRetry={retryToLoadLastPinnedItemsPage}
								hasExtraTopPadding
							/>
						)}
					</HubOverviewLayout.Section>
				)}
				{currentOtherItemsToRender.length > 0 && (
					<HubOverviewLayout.Section trackingId="other-section">
						{hasPinnedItems && (
							<HubOverviewLayout.SectionTitle trackingId="other-section-title">
								{localize("_Other")}
							</HubOverviewLayout.SectionTitle>
						)}
						<List
							trackingId="announcement-list-other"
							items={currentOtherItemsToRender}
							onNavigateToItem={onNavigateToItem}
							hasMoreItems={hasMoreOtherItemsOnServer && !hasOtherItemsLazyLoadError}
							isBusy={getOtherItemsLoadingState() === "loading"}
							onLoadMore={loadMoreOtherHandler}
							shouldReserveSpaceForFloatingActionButton={hasFloatingButton}
							timezone={timezone}
						/>
						{hasOtherItemsLazyLoadError && (
							<NoResults
								trackingId="has-lazy-loading-error"
								icon="gear"
								title={localize("_NotAbleToRetrieveAnnouncements")}
								message={localize("_WeWereUnableToRetrieveYourAnnouncements")}
								onRetry={retryToLoadLastOtherItemsPage}
								hasExtraTopPadding
							/>
						)}
					</HubOverviewLayout.Section>
				)}
			</>
		);
	}

	if (otherItemsLoadingState === "loading") {
		return <BusyIndicator />;
	}

	if (otherItemsLoadingState === "error") {
		return (
			<NoResults
				trackingId="has-error"
				icon="gear"
				title={localize("_NotAbleToRetrieveAnnouncements")}
				message={localize("_WeWereUnableToRetrieveYourAnnouncements")}
				onRetry={reloadHandler}
				height="fill"
			/>
		);
	}

	if (searchTerm) {
		return (
			<NoResults
				trackingId="no-search-results"
				icon="magnifying-glass"
				title={localize("_NoSearchResults")}
				message={localize("_PleaseTrySearchingWithAnotherTerm")}
				height="fill"
			/>
		);
	}

	return (
		<NoResults
			trackingId="no-announcements-yet"
			icon="bullhorn"
			title={localize("_NoAnnouncementsYet")}
			message={getNoAnnouncementsMessage(activeFilter, selectedCategoryId)}
			height="fill"
		/>
	);
};
OverviewSections.displayName = "OverviewSections";

const PAGE_SIZE = 24;
const DEFAULT_PINNED_ITEMS_MOBILE_COUNT = 2;
const MAX_PINNED_ITEMS_IN_A_ROW_COUNT = 10;

type MoreButtonProps = {
	isExpanded: boolean;
	onClick(): void;
	isMobile?: boolean;
};

const MoreButton = (props: MoreButtonProps): ReactElement => {
	const {
		isExpanded,
		onClick,
		isMobile = false
	} = props;

	const text: string = isExpanded
		? localize("_ShowLessPinnedAnnouncements")
		: localize("_ShowAllPinnedAnnouncements");

	if (isMobile) {
		return (
			<HubListButton
				onClick={onClick}
				trackingId="more-pinned-items-mobile"
			>
				{text}
			</HubListButton>
		);
	}

	return (
		<div className={styles.moreButtonContainer}>
			<Button
				kind="transparent"
				icon={isExpanded
					? "chevron-up"
					: "chevron-down"
				}
				onClick={onClick}
				text={text}
				trackingId="more-pinned-items"
			/>
		</div>
	);
};
MoreButton.displayName = "MoreButton";

const pinDateSorting: DataSourceSortableState<AnnouncementPreviewItemModel> = {
	direction: DataSourceSortDirection.Descending,
	propertyName: "pinDate"
};

function getNoAnnouncementsMessage(
	filter: ActiveFilter | null,
	categoryFilter: number | null
): string {
	let keySelection: Record<0 | 1, LocalizationKey>;
	if (filter?.kind === "author") {
		keySelection = {
			[0]: "_TheAuthorDidNotPublishAnyItemsYet",
			[1]: "_TheAuthorDidNotPublishAnyItemsInThisCategoryYet"
		};
	} else if (filter?.kind === "tag") {
		keySelection = {
			[0]: "_ThereAreNoPublishedItemsWithThisTag",
			[1]: "_ThereAreNoPublishedItemsWithThisTagInThisCategory"
		};
	} else {
		keySelection = {
			[0]: "_ThereAreNoPublishedItemsYet",
			[1]: "_ThereAreNoPublishedItemsInThisCategoryYet"
		};
	}

	return localize(
		keySelection[isNil(categoryFilter) ? 0 : 1]
	);
}